import React, { useState, useEffect } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { getLocationBySlug, getAllLocations } from '../data/locations';
import { galleryImages } from '../data/mock';
import { benefits } from '../data/locations';
import SEOHead from '../components/SEOHead';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import ContactSection from '../components/ContactSection';
import { Heart, Star, Sparkles, Trophy, Users, Music, ArrowRight, ChevronRight, Globe, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';

const iconMap = {
  heart: Heart,
  star: Star,
  sparkles: Sparkles,
  trophy: Trophy,
  users: Users,
  music: Music,
};

const LocationPage = () => {
  const { slug } = useParams();
  const location = getLocationBySlug(slug);
  
  // Determine if this location uses French (Tunis)
  const isFrenchLocation = location?.defaultLanguage === 'fr';
  
  // Set initial language based on location
  const [language, setLanguage] = useState(isFrenchLocation ? 'fr' : 'en');
  
  // Update language when location changes
  useEffect(() => {
    if (location) {
      setLanguage(location.defaultLanguage === 'fr' ? 'fr' : 'en');
    }
  }, [location]);
  
  // Translation function that supports French for Tunis
  const t = (en, frOrId) => {
    if (isFrenchLocation) {
      return language === 'fr' ? frOrId : en;
    }
    // For non-French locations, frOrId is Indonesian
    return language === 'en' ? en : frOrId;
  };
  
  // Get text with French support
  const getText = (enKey, frKey, idKey) => {
    if (isFrenchLocation) {
      const frValue = frKey;
      const enValue = enKey;
      return language === 'fr' ? (frValue || enValue) : enValue;
    }
    return language === 'en' ? enKey : (idKey || enKey);
  };
  
  const toggleLanguage = () => {
    if (isFrenchLocation) {
      setLanguage(prev => prev === 'fr' ? 'en' : 'fr');
    } else {
      setLanguage(prev => prev === 'en' ? 'id' : 'en');
    }
  };

  // Redirect to home if location not found
  if (!location) {
    return <Navigate to="/" replace />;
  }

  const { schoolInfo, instructor, classes, testimonials, images, seo, hasGallery, galleryTitle } = location;

  // UI Text translations
  const uiText = {
    home: { en: 'Home', fr: 'Accueil', id: 'Beranda' },
    locations: { en: 'Locations', fr: 'Nos Studios', id: 'Lokasi' },
    enrollNow: { en: 'Enroll Now', fr: "S'inscrire", id: 'Daftar Sekarang' },
    viewClasses: { en: 'View Classes', fr: 'Voir les Cours', id: 'Lihat Kelas' },
    students: { en: 'Students', fr: 'Élèves', id: 'Siswa' },
    years: { en: 'Years', fr: 'Années', id: 'Tahun' },
    ageRange: { en: 'Age Range', fr: 'Tranche d\'Âge', id: 'Rentang Usia' },
    aboutStudio: { en: 'About Our Studio', fr: 'Notre Studio', id: 'Tentang Studio Kami' },
    balletExcellence: { en: `Ballet Excellence in ${location.city}`, fr: `Excellence du Ballet à ${location.city}`, id: `Keunggulan Balet di ${location.city}` },
    happyDancers: { en: 'Happy Dancers', fr: 'Danseurs Heureux', id: 'Penari Bahagia' },
    ourPrograms: { en: 'Our Programs', fr: 'Nos Programmes', id: 'Program Kami' },
    classesIn: { en: `Classes in ${location.city}`, fr: `Cours à ${location.city}`, id: `Kelas di ${location.city}` },
    chooseProgram: { en: 'Choose the perfect program for your child based on their age and experience level.', fr: 'Choisissez le programme parfait pour votre enfant en fonction de son âge et de son niveau d\'expérience.', id: 'Pilih program yang sempurna untuk anak Anda berdasarkan usia dan tingkat pengalaman mereka.' },
    yearsOld: { en: 'years', fr: 'ans', id: 'tahun' },
    inquireNow: { en: 'Inquire Now', fr: 'Renseignements', id: 'Tanyakan Sekarang' },
    meetInstructor: { en: 'Meet Our Instructor', fr: 'Notre Instructrice', id: 'Kenali Instruktur Kami' },
    bookTrial: { en: 'Book a Trial Class', fr: 'Réserver un Cours d\'Essai', id: 'Pesan Kelas Percobaan' },
    testimonials: { en: 'Testimonials', fr: 'Témoignages', id: 'Testimoni' },
    whatParentsSay: { en: 'What Parents Say', fr: 'Ce Que Disent les Parents', id: 'Apa Kata Orang Tua' },
    gallery: { en: 'Gallery', fr: 'Galerie', id: 'Galeri' },
    viewFullGallery: { en: 'View Full Gallery', fr: 'Voir la Galerie Complète', id: 'Lihat Galeri Lengkap' },
  };

  const getUI = (key) => {
    const texts = uiText[key];
    if (!texts) return key;
    if (isFrenchLocation) {
      return language === 'fr' ? texts.fr : texts.en;
    }
    return language === 'en' ? texts.en : texts.id;
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead seo={seo} location={location} language={language} />
      
      {/* Custom Header for Location Page */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-transparent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-3">
              <img
                src={schoolInfo.logoUrl}
                alt={schoolInfo.name}
                className="w-12 h-12 rounded-full object-cover"
              />
              <span className="font-bold text-lg tracking-tight text-white hidden sm:block">
                {schoolInfo.name}
              </span>
            </Link>
            
            <nav className="hidden md:flex items-center gap-8">
              <a href="#about" className="text-sm font-medium text-white hover:text-[#f0c8c8] transition-colors">
                {isFrenchLocation ? (language === 'fr' ? 'À Propos' : 'About') : (language === 'en' ? 'About' : 'Tentang')}
              </a>
              <a href="#classes" className="text-sm font-medium text-white hover:text-[#f0c8c8] transition-colors">
                {isFrenchLocation ? (language === 'fr' ? 'Cours' : 'Classes') : (language === 'en' ? 'Classes' : 'Kelas')}
              </a>
              <a href="#instructor" className="text-sm font-medium text-white hover:text-[#f0c8c8] transition-colors">
                {isFrenchLocation ? (language === 'fr' ? 'Instructrice' : 'Instructor') : (language === 'en' ? 'Instructor' : 'Instruktur')}
              </a>
              {hasGallery && (
                <a href="#gallery" className="text-sm font-medium text-white hover:text-[#f0c8c8] transition-colors">
                  {isFrenchLocation ? (language === 'fr' ? 'Galerie' : 'Gallery') : (language === 'en' ? 'Gallery' : 'Galeri')}
                </a>
              )}
              <a href="#contact" className="text-sm font-medium text-white hover:text-[#f0c8c8] transition-colors">
                Contact
              </a>
              <button
                onClick={toggleLanguage}
                className="flex items-center gap-2 text-sm font-medium text-white hover:text-[#f0c8c8] transition-colors"
              >
                <Globe className="w-4 h-4" />
                {isFrenchLocation ? (language === 'fr' ? 'EN' : 'FR') : (language === 'en' ? 'ID' : 'EN')}
              </button>
            </nav>
          </div>
        </div>
      </header>
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={images.hero}
            alt={`Studio Ballet Izora ${location.city}`}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70" />
        </div>
        
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center gap-2 text-white/70 mb-6">
            <Link to="/" className="hover:text-white transition-colors">
              {getUI('home')}
            </Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/#locations" className="hover:text-white transition-colors">
              {getUI('locations')}
            </Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-white">{location.city}</span>
          </div>
          
          <img
            src={schoolInfo.logoUrl}
            alt={schoolInfo.name}
            className="w-20 h-20 sm:w-24 sm:h-24 rounded-full mx-auto mb-6 border-4 border-white/30 shadow-2xl"
          />
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4 tracking-tight">
            {schoolInfo.name}
          </h1>
          <p className="text-2xl sm:text-3xl text-[#f0c8c8] mb-4">
            {location.city}, {location.country}
          </p>
          <p className="text-lg sm:text-xl text-white/80 mb-8 max-w-2xl mx-auto">
            {isFrenchLocation 
              ? (language === 'fr' ? schoolInfo.taglineFr : schoolInfo.taglineEn)
              : (language === 'en' ? schoolInfo.taglineEn : schoolInfo.taglineId)
            }
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="#contact">
              <Button className="bg-[#8B1C1C] hover:bg-[#6d1616] text-white px-8 py-6 rounded-full text-lg font-semibold transition-all">
                {getUI('enrollNow')}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </a>
            <a href="#classes">
              <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#8B1C1C] px-8 py-6 rounded-full text-lg font-semibold transition-all">
                {getUI('viewClasses')}
              </Button>
            </a>
          </div>
        </div>
        
        {/* Stats */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent py-12">
          <div className="max-w-4xl mx-auto px-4 grid grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-3xl sm:text-4xl font-bold text-white mb-1">{schoolInfo.studentsCount}</div>
              <div className="text-white/70 text-sm sm:text-base">{getUI('students')}</div>
            </div>
            <div>
              <div className="text-3xl sm:text-4xl font-bold text-white mb-1">{schoolInfo.yearsExperience}</div>
              <div className="text-white/70 text-sm sm:text-base">{getUI('years')}</div>
            </div>
            <div>
              <div className="text-3xl sm:text-4xl font-bold text-white mb-1">{schoolInfo.ageRange}</div>
              <div className="text-white/70 text-sm sm:text-base">{getUI('ageRange')}</div>
            </div>
          </div>
        </div>
      </section>
      
      {/* About Section */}
      <section id="about" className="py-24 bg-[#f7f5f2]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-block text-[#8B1C1C] font-semibold text-sm uppercase tracking-wider mb-4">
                {getUI('aboutStudio')}
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#1e1919] mb-6">
                {getUI('balletExcellence')}
              </h2>
              <p className="text-[#736c64] text-lg leading-relaxed mb-8">
                {isFrenchLocation 
                  ? (language === 'fr' ? schoolInfo.descriptionFr : schoolInfo.descriptionEn)
                  : (language === 'en' ? schoolInfo.descriptionEn : schoolInfo.descriptionId)
                }
              </p>
              
              <div className="grid grid-cols-2 gap-6">
                {benefits.slice(0, 4).map((benefit, index) => {
                  const Icon = iconMap[benefit.icon] || Heart;
                  return (
                    <div key={index} className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-[#8B1C1C]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-[#8B1C1C]" />
                      </div>
                      <div>
                        <p className="font-semibold text-[#1e1919] text-sm">
                          {language === 'en' ? benefit.titleEn : benefit.titleId}
                        </p>
                        <p className="text-[#736c64] text-xs">
                          {language === 'en' ? benefit.descriptionEn : benefit.descriptionId}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            
            <div className="relative">
              <img
                src={images.about}
                alt={`About Studio Ballet Izora ${location.city}`}
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#8B1C1C] text-white p-6 rounded-xl shadow-xl">
                <p className="text-3xl font-bold">{schoolInfo.studentsCount}</p>
                <p className="text-white/80">{getUI('happyDancers')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Classes Section */}
      <section id="classes" className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block text-[#8B1C1C] font-semibold text-sm uppercase tracking-wider mb-4">
              {getUI('ourPrograms')}
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1e1919] mb-6">
              {getUI('classesIn')}
            </h2>
            <p className="text-[#736c64] text-lg max-w-2xl mx-auto">
              {getUI('chooseProgram')}
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {classes.map((classItem) => (
              <div key={classItem.id} className="bg-[#f7f5f2] rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={classItem.image}
                    alt={isFrenchLocation 
                      ? (language === 'fr' ? classItem.titleFr : classItem.titleEn)
                      : (language === 'en' ? classItem.titleEn : classItem.titleId)
                    }
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-4 right-4 bg-[#8B1C1C] text-white px-3 py-1 rounded-full text-sm font-semibold">
                    {classItem.ageRange} {getUI('yearsOld')}
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-[#1e1919] mb-3">
                    {isFrenchLocation 
                      ? (language === 'fr' ? classItem.titleFr : classItem.titleEn)
                      : (language === 'en' ? classItem.titleEn : classItem.titleId)
                    }
                  </h3>
                  <p className="text-[#736c64] text-sm leading-relaxed mb-4">
                    {isFrenchLocation 
                      ? (language === 'fr' ? classItem.descriptionFr : classItem.descriptionEn)
                      : (language === 'en' ? classItem.descriptionEn : classItem.descriptionId)
                    }
                  </p>
                  <a href="#contact" className="text-[#8B1C1C] font-semibold text-sm flex items-center gap-1 hover:gap-2 transition-all">
                    {getUI('inquireNow')}
                    <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Instructor Section */}
      <section id="instructor" className="py-24 bg-[#f7f5f2]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <span className="inline-block text-[#8B1C1C] font-semibold text-sm uppercase tracking-wider mb-4">
                {getUI('meetInstructor')}
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#1e1919] mb-2">
                {instructor.name}
              </h2>
              <p className="text-[#8B1C1C] font-semibold mb-6">
                {isFrenchLocation 
                  ? (language === 'fr' ? instructor.titleFr : instructor.titleEn)
                  : (language === 'en' ? instructor.titleEn : instructor.titleId)
                }
              </p>
              <p className="text-[#736c64] text-lg leading-relaxed mb-8">
                {isFrenchLocation 
                  ? (language === 'fr' ? instructor.bioFr : instructor.bioEn)
                  : (language === 'en' ? instructor.bioEn : instructor.bioId)
                }
              </p>
              <a href="#contact">
                <Button className="bg-[#8B1C1C] hover:bg-[#6d1616] text-white px-6 py-3 rounded-full font-semibold transition-all">
                  {getUI('bookTrial')}
                </Button>
              </a>
            </div>
            <div className="order-1 lg:order-2">
              <img
                src={instructor.image}
                alt={instructor.name}
                className="rounded-2xl shadow-2xl w-full max-w-md mx-auto"
              />
            </div>
          </div>
        </div>
      </section>
      
      {/* Locations Worldwide Section */}
      <section id="locations" className="py-24 bg-[#f7f5f2]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block text-[#8B1C1C] font-semibold text-sm uppercase tracking-wider mb-4">
              {isFrenchLocation 
                ? (language === 'fr' ? 'Nos Studios' : 'Our Studios')
                : (language === 'en' ? 'Our Studios' : 'Studio Kami')
              }
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1e1919] mb-6">
              {isFrenchLocation 
                ? (language === 'fr' ? 'Nos Emplacements dans le Monde' : 'Locations Worldwide')
                : (language === 'en' ? 'Locations Worldwide' : 'Lokasi di Seluruh Dunia')
              }
            </h2>
            <p className="text-[#736c64] text-lg max-w-2xl mx-auto">
              {isFrenchLocation 
                ? (language === 'fr' 
                    ? 'Studio Ballet Izora apporte une éducation de ballet de classe mondiale aux jeunes danseurs en Asie et en Afrique.'
                    : 'Studio Ballet Izora brings world-class ballet education to young dancers across Asia and Africa.')
                : (language === 'en' 
                    ? 'Studio Ballet Izora brings world-class ballet education to young dancers across Asia and Africa.'
                    : 'Studio Ballet Izora membawa pendidikan balet kelas dunia kepada penari muda di seluruh Asia dan Afrika.')
              }
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {getAllLocations().map((loc) => (
              <Link
                key={loc.id}
                to={loc.slug === 'surabaya' ? '/' : `/standorte/${loc.slug}`}
                className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={loc.images.hero}
                    alt={`Studio Ballet Izora ${loc.city}`}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="text-xl font-bold text-white">{loc.city}</h3>
                    <p className="text-white/80 text-sm">{loc.country}</p>
                  </div>
                  {loc.slug === slug && (
                    <div className="absolute top-4 right-4 bg-[#8B1C1C] text-white text-xs px-3 py-1 rounded-full font-semibold">
                      {isFrenchLocation 
                        ? (language === 'fr' ? 'Vous êtes ici' : 'You are here')
                        : (language === 'en' ? 'You are here' : 'Anda di sini')
                      }
                    </div>
                  )}
                  {loc.slug === 'surabaya' && (
                    <div className="absolute top-4 left-4 bg-[#8B1C1C] text-white text-xs px-3 py-1 rounded-full font-semibold">
                      {isFrenchLocation 
                        ? (language === 'fr' ? 'Studio Principal' : 'Main Studio')
                        : (language === 'en' ? 'Main Studio' : 'Studio Utama')
                      }
                    </div>
                  )}
                </div>
                
                <div className="p-6">
                  <div className="flex items-center gap-2 text-[#736c64] mb-4">
                    <MapPin className="w-4 h-4" />
                    <span className="text-sm">{loc.schoolInfo.location}</span>
                  </div>
                  
                  <p className="text-[#736c64] text-sm mb-4 line-clamp-2">
                    {isFrenchLocation 
                      ? (language === 'fr' 
                          ? (loc.schoolInfo.descriptionFr || loc.schoolInfo.descriptionEn).substring(0, 100)
                          : loc.schoolInfo.descriptionEn.substring(0, 100))
                      : (language === 'en' 
                          ? loc.schoolInfo.descriptionEn.substring(0, 100)
                          : (loc.schoolInfo.descriptionId || loc.schoolInfo.descriptionEn).substring(0, 100))
                    }...
                  </p>
                  
                  <div className="flex items-center justify-between">
                    <div className="text-sm">
                      <span className="font-semibold text-[#1e1919]">{loc.schoolInfo.studentsCount}</span>
                      <span className="text-[#736c64] ml-1">
                        {isFrenchLocation 
                          ? (language === 'fr' ? 'Élèves' : 'Students')
                          : (language === 'en' ? 'Students' : 'Siswa')
                        }
                      </span>
                    </div>
                    <span className="text-[#8B1C1C] font-semibold text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
                      {loc.slug === slug 
                        ? (isFrenchLocation 
                            ? (language === 'fr' ? 'Vous êtes ici' : 'You are here')
                            : (language === 'en' ? 'You are here' : 'Anda di sini'))
                        : (isFrenchLocation 
                            ? (language === 'fr' ? 'Visiter' : 'Visit Studio')
                            : (language === 'en' ? 'Visit Studio' : 'Kunjungi Studio'))
                      }
                      <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
      
      {/* Testimonials Section */}
      <section className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block text-[#8B1C1C] font-semibold text-sm uppercase tracking-wider mb-4">
              {getUI('testimonials')}
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1e1919] mb-6">
              {getUI('whatParentsSay')}
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <div key={testimonial.id} className="bg-[#f7f5f2] p-8 rounded-2xl">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-[#8B1C1C] text-[#8B1C1C]" />
                  ))}
                </div>
                <p className="text-[#736c64] italic mb-6 leading-relaxed">
                  "{isFrenchLocation 
                    ? (language === 'fr' ? testimonial.textFr : testimonial.textEn)
                    : (language === 'en' ? testimonial.textEn : testimonial.textId)
                  }"
                </p>
                <p className="font-semibold text-[#1e1919]">
                  {isFrenchLocation 
                    ? (language === 'fr' ? testimonial.nameFr : testimonial.nameEn)
                    : (language === 'en' ? testimonial.nameEn : testimonial.nameId)
                  }
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Gallery Section */}
      {hasGallery && (
        <section id="gallery" className="py-24 bg-[#f7f5f2]">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <span className="inline-block text-[#8B1C1C] font-semibold text-sm uppercase tracking-wider mb-4">
                {getUI('gallery')}
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#1e1919] mb-4">
                {isFrenchLocation 
                  ? (language === 'fr' ? galleryTitle.fr : galleryTitle.en)
                  : (language === 'en' ? galleryTitle.en : galleryTitle.id)
                }
              </h2>
            </div>
            
            {/* Gallery Preview */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {galleryImages.slice(0, 8).map((image, index) => (
                <div key={index} className="aspect-square rounded-xl overflow-hidden">
                  <img
                    src={image}
                    alt={`Gallery ${index + 1}`}
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                  />
                </div>
              ))}
            </div>
            
            <div className="text-center">
              <Link to={`/standorte/${slug}/gallery`}>
                <Button className="bg-[#8B1C1C] hover:bg-[#6d1616] text-white px-8 py-3 rounded-full font-semibold transition-all">
                  {getUI('viewFullGallery')}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}
      
      {/* Contact Section */}
      <ContactSection location={location} language={language} isFrenchLocation={isFrenchLocation} />
      
      <Footer location={location} />
      <WhatsAppButton location={location} />
    </div>
  );
};

export default LocationPage;
